import { useState, useRef, useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import { FaChevronDown, FaSearch } from "react-icons/fa";

export default function Dropdown({
  topics,
  onModuleSelect,
  value = null,
  variant,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [dropUp, setDropUp] = useState(false);
  const [shouldRenderDropdown, setShouldRenderDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const searchInputRef = useRef(null);

  const isPlatformVariant = variant === "PLATFORM_SELECTOR";

  const filteredTopics = topics.filter((topic) =>
    topic.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTopicSelect = (topic) => {
    setIsOpen(false);
    setSearchTerm("");
    onModuleSelect(topic);
  };

  const clearSelection = () => {
    onModuleSelect("");
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !triggerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Measure space BEFORE rendering
  const handleToggleDropdown = () => {
    if (!isOpen) {
      // Measure before rendering
      const rect = triggerRef.current.getBoundingClientRect();
      const estimatedHeight = 300;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropUp(
        spaceBelow < estimatedHeight && spaceAbove > estimatedHeight / 2
      );
      setShouldRenderDropdown(true);
      setTimeout(() => setIsOpen(true), 0); // allow DOM update
    } else {
      setIsOpen(false);
    }
  };

  return (
    <div className="w-1/4" ref={dropdownRef}>
      <div className="relative max-w-md">
        <div
          ref={triggerRef}
          onClick={handleToggleDropdown}
          className="flex items-center p-2 justify-between bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-sm cursor-pointer hover:border-blue-400 transition-colors"
        >
          <span
            className={`font-medium ${
              value
                ? "text-gray-900 dark:text-white"
                : "text-gray-500 dark:text-gray-400"
            }`}
          >
            {value
              ? value
              : isPlatformVariant
              ? "Select platform"
              : "Select topic"}
          </span>
          <div className="flex items-center gap-2">
            {value && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelection();
                }}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <IoMdClose size={16} className="text-gray-500" />
              </button>
            )}
            <FaChevronDown
              size={20}
              className={`text-gray-500 transition-transform ${
                isOpen ? "rotate-180" : ""
              }`}
            />
          </div>
        </div>

        {/* Dropdown */}
        {shouldRenderDropdown && isOpen && (
          <div
            className={`absolute left-0 right-0 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-sm shadow-lg z-50 max-h-80 overflow-hidden transition-all ${
              dropUp ? "bottom-full mb-2" : "top-full mt-2"
            }`}
          >
            {/* Search Input */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-600">
              <div className="relative">
                <FaSearch
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full text-white pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
            </div>

            {/* Topics List */}
            <div className="max-h-60 overflow-y-auto">
              {filteredTopics.length > 0 ? (
                filteredTopics.map((topic, index) => (
                  <button
                    key={index}
                    onClick={() => handleTopicSelect(topic)}
                    className={`w-full text-left px-4 py-3 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium ${
                      value === topic
                        ? "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300"
                        : "text-gray-700 dark:text-gray-300"
                    }`}
                  >
                    {topic}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                  No {isPlatformVariant ? "platform" : "topic"} found matching "
                  {searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
